import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../hooks/useTheme";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, FluentProvider, Text, Field, Textarea } from "@fluentui/react-components";
import { ErrorCircle12Filled } from "@fluentui/react-icons";
import { Logger } from "../../services/Logger";
import { ConfigurationService } from "../../services/ConfigurationService";
import { OutlookApiService } from "../../services/OutlookApiService";
import { AttachmentDataHelpers } from "../../helpers/AttachmentDataHelpers";
import EditorFluent9 from "../shared/editor/EditorFluent9";
import { EmailApiService } from "../../services/NewformaApi/EmailApiService";
import { MsGraphApiService } from "../../services/MsGraphApiService";
import { AnalyticsManager } from "../../services/AnalyticsManager";
import { AppPage } from "../shared/navigationHeader/NavigationHeaderComponent";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { NewformaToastIntent } from "../_base/Toast";
import { ProjectPickerWithLoader } from "../shared/projectPicker/ProjectPickerWithLoader";
import { SmartFilingManagerFluent9 } from "../../services/SmartFiling/SmartFilingManagerFluent9";
import { InvalidateCacheService } from "../../services/NewformaApi/InvalidateCacheService";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { IProjectData } from "../../models/shared/IProjectData";
import { ProjectsCacheKeys } from "../../models/StorageKeys";
import { NrnServiceWrapper } from "../../services/NrnServiceWrapper";
import { DateOffsetType, NumberEntry, RfiSettings } from "../../models/projects/ProjectSettingsResponse";
import { DateHelper } from "../../helpers/DateHelpers";
import { NewformaInput } from "../_base/NewformaInput";
import { NewformaDatePicker } from "../_base/NewformaDatePicker";
import { NewformaCheckbox } from "../_base/Checkbox";
import { NewformaButton } from "../_base/Button";
import { NewformaLink } from "../_base/Link";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import { RfiApiService } from "../../services/NewformaApi/RfiApiService";
import { LogRfiParams } from "../../models/workflow/rfi/LogRfiParams";
import { LogForwardRfiRequest } from "../../models/workflow/rfi/ForwardRfiRequest";
import { Keyword } from "../../models/Keyword";
import { ApiRequestErrorLevel } from "../../models/ApiRequestErrorWithMessage";
import { FormValidationHelpers } from "../../helpers/FormValidationHelpers";
import { DetailedKeyword, KeywordListType, ProjectKeywordsResponse } from "../../models/ProjectKeywordsResponse";
import { Contract } from "../../models/projects/ContractsResponse";
import { ProjectHelper } from "../../helpers/ProjectHelper";
import { WorkflowActionType } from "../../models/workflow/WorkflowActionType";
import NewformaDropdown, { NewformaDropdownOption } from "../_base/NewformaDropdown";
import { MultiSelectTagPicker } from "../_base/MultiSelectTagPicker";
import { IPersonaProps } from "../shared/assignee/AssigneeFluent9";
import FromAndToFluent9Simple from "../shared/fromAndToComponent/FromAndToFluent9Simple";
import { useProjectTeamMembers } from "../../hooks/useProjectTeamMembers";
import ForwardRfiFluent9 from "./forwardRfi/ForwardRfiFluent9";
import {
    ReminderFluent9,
    getCurrentLocalDay,
    getMaxReminderDays,
    MIN_REMINDER_DAYS,
    useCurrentLocalDay,
} from "../shared/reminder/ReminderFluent9";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        userSelect: "none",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    content: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: `4px ${tokens.spacingHorizontalXXL} 0 ${tokens.spacingHorizontalXXL}`,
        gap: tokens.spacingVerticalL,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        flexShrink: 0,
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalXXL,
        paddingRight: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    // Groups the date row with the Clear link beneath it so they sit closer together than the
    // standard gap between form fields.
    dateSection: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalXS,
    },
    // Groups the date/Clear link block with the reminder toggle beneath it (the reminder is relative
    // to the Due Date above it) so they read as one unit instead of leaving the standard field-to-field
    // gap, which looked like a stray blank line under the Clear link.
    reminderSection: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalS,
    },
    // Two equal columns so each date field occupies half the width: Received Date on the left,
    // Due Date on the right. minmax(0, 1fr) (rather than the default minmax(auto, 1fr)) lets each
    // column shrink below the date input's intrinsic width so the row never overflows the pane.
    dateRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        columnGap: tokens.spacingHorizontalL,
    },
    dateField: {
        minWidth: 0,
    },
    datePicker: {
        width: "100%",
        minWidth: 0,
    },
    clearDateLink: {
        alignSelf: "flex-end",
        fontSize: tokens.fontSizeBase200,
    },
    forwardCard: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground1Hover,
        borderRadius: tokens.borderRadiusMedium,
    },
    forwardCheckbox: {
        display: "flex",
        alignItems: "center",
        // Fluent's medium Checkbox indicator is 16px with a 6px margin; the label line is 20px.
        // Offset so the indicator centres on the first label line: (20 - 16) / 2 - 6 = -4px.
        marginTop: "-4px",
        marginLeft: "-6px",
    },
    forwardTextColumn: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalXXS,
    },
    forwardLabel: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
    },
    forwardSubtext: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
    noOptionWarning: {
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        color: tokens.colorStatusWarningForeground1,
    },
    // Type and Sender Number sit side by side, each constrained to half the row width. Mirrors the
    // legacy newforma-rfiTypeDropdown/newforma-rfiSenderNumber 50/50 split.
    typeSenderRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        columnGap: tokens.spacingHorizontalL,
    },
    typeField: {
        minWidth: 0,
    },
    senderNumberField: {
        minWidth: 0,
    },
});

// Sorts keyword options the same way the shared KeywordsFluent9 picker does: by displayOrder,
// then alphabetically for ties, so the Fluent 9 pickers present options in the legacy order.
function sortDetailedKeywords(items: DetailedKeyword[]): DetailedKeyword[] {
    return [...items].sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
}

// Mirrors KeywordsFluent9.handleSelectionChange: resolves the string names emitted by
// MultiSelectTagPicker back into Keyword payload entries. Already-selected keywords keep their
// original metadata, known options use the loaded list metadata, and otherwise a typed value is
// only accepted as a custom { type: "generic" } keyword when the project list allows custom values.
function resolveSelectedKeywords(
    selectedNames: string[],
    current: Keyword[],
    response: ProjectKeywordsResponse | null
): Keyword[] {
    const valueMap = new Map(current.map((kw) => [kw.name, kw]));
    const optionMap = new Map((response?.items ?? []).map((kw) => [kw.name, kw]));
    const result: Keyword[] = [];
    for (const name of selectedNames) {
        const already = valueMap.get(name);
        if (already) {
            result.push(already);
            continue;
        }
        const option = optionMap.get(name);
        if (option) {
            result.push({ name: option.name, type: "generic" });
            continue;
        }
        if (response?.allowCustom) {
            result.push({ name, type: "generic" });
        }
    }
    return result;
}

const DEFAULT_REMINDER_DAYS = 2;

// Mirrors legacy RfiComponent.getDueDate(): the default due date is offset from the received date by
// the project's RFI due-date settings (14 calendar days when the project has none).
function getDefaultDueDate(
    receivedDate: Date | undefined,
    settings: Pick<RfiSettings, "dueDateOffsetInDays" | "dueDateOffsetType"> | null
): Date | undefined {
    if (!receivedDate) {
        return undefined;
    }
    const offset = settings?.dueDateOffsetInDays ?? 14;
    return settings?.dueDateOffsetType === DateOffsetType.business
        ? DateHelper.addBusinessDays(receivedDate, offset)
        : DateHelper.getDateWithOffset(receivedDate, offset);
}

// Mirrors legacy RfiComponent.getProjectSettings()'s forwardDueDate: the Forward for Review page's
// default Due Back Date is offset from today by the project's review-response due-date settings,
// capped at the RFI's own due date so the forward-review deadline never lands after the RFI is due.
function getDefaultForwardDueDate(
    dueDate: Date | undefined,
    settings: Pick<RfiSettings, "reviewResponseDueDateOffsetInDays" | "dueDateOffsetType"> | null
): Date | undefined {
    const offset = settings?.reviewResponseDueDateOffsetInDays;
    if (!offset) {
        return undefined;
    }
    const forwardDueDate =
        settings?.dueDateOffsetType === DateOffsetType.business
            ? DateHelper.addBusinessDays(getCurrentLocalDay(), offset)
            : DateHelper.getDateWithOffset(getCurrentLocalDay(), offset);
    return dueDate && forwardDueDate > dueDate ? dueDate : forwardDueDate;
}

interface RfiFluent9Props extends LocalizeContextProps {
    logger: Logger;
    onExpiredSession: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    onSetNavigationPage: (page: AppPage) => void;
    mailboxItem: MailboxItem | null;
    emailApiService: EmailApiService;
    msGraphApiService: MsGraphApiService;
    analyticsManager: AnalyticsManager;
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    projectsService: IProjectsService;
    officeWrapper: OfficeWrapper;
    nrnServiceWrapper: NrnServiceWrapper;
    rfiApiService: RfiApiService;
    formValidationHelpers: FormValidationHelpers;
    configService: ConfigurationService;
    outlookApiService: OutlookApiService;
    isNL1221Supported: boolean;
    culture: string;
}

const RfiFluent9Inner: React.FC<RfiFluent9Props> = (props) => {
    const styles = useStyles();
    const { theme } = useTheme();
    const currentLocalDay = useCurrentLocalDay();

    const [selectedProject, setSelectedProject] = useState<IProjectData | undefined>(undefined);
    const [number, setNumber] = useState<string>("");
    const [numberEntryMode, setNumberEntryMode] = useState<NumberEntry | null>(null);
    const [receivedDate, setReceivedDate] = useState<Date | undefined>(undefined);
    const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
    const [isReminderEnabled, setIsReminderEnabled] = useState<boolean>(true);
    const [reminderDays, setReminderDays] = useState<number>(DEFAULT_REMINDER_DAYS);
    // Due Date drives the reminder, so it can never be set before today, even when the received
    // date (otherwise the picker's minDate) is further in the past.
    const dueDateMinDate = useMemo(
        () => (receivedDate && DateHelper.isDateAfter(receivedDate, currentLocalDay) ? receivedDate : currentLocalDay),
        [receivedDate, currentLocalDay]
    );
    // Caps the reminder to "days before due" that still land after today, mirroring
    // ForwardRfiFluent9's getMaxReminderDays: a reminder isn't offered once the due date is too soon.
    const maxReminderDays = useMemo(() => getMaxReminderDays(dueDate, undefined, currentLocalDay), [
        dueDate,
        currentLocalDay,
    ]);
    const isReminderLimited = !!dueDate && maxReminderDays < MIN_REMINDER_DAYS;
    // True when isReminderEnabled was turned off because the due date was missing/too soon, not by
    // the user's own choice — so restoring a usable due date turns the reminder back on, matching
    // legacy ReminderComponent's componentDidUpdate (re-enables whenever its `disabled` prop clears).
    const wasReminderAutoDisabledRef = useRef<boolean>(false);
    useEffect(() => {
        if (isReminderLimited) {
            setIsReminderEnabled(false);
            wasReminderAutoDisabledRef.current = true;
            return;
        }
        if (dueDate && wasReminderAutoDisabledRef.current) {
            setIsReminderEnabled(true);
            wasReminderAutoDisabledRef.current = false;
        }
        setReminderDays((prev) => Math.min(prev, maxReminderDays));
    }, [dueDate, maxReminderDays, isReminderLimited]);
    // Forward for Review page's own due date / reminder. Owned here (not by ForwardRfiFluent9) so
    // the values survive the panel being unmounted when the user clicks Back. ForwardRfiFluent9
    // derives its own max-days clamping from these via its onReminderEnabledChange/onReminderDaysChange props.
    const [forwardDueDate, setForwardDueDate] = useState<Date | undefined>(undefined);
    const [isForwardReminderEnabled, setIsForwardReminderEnabled] = useState<boolean>(false);
    const [forwardReminderDays, setForwardReminderDays] = useState<number>(DEFAULT_REMINDER_DAYS);
    // mirrors legacy RfiComponent.forwardEmailRecipients: the Forward for Review page's own To field,
    // independent of the main page's To and not preloaded from it (matches legacy's empty default).
    const [forwardTo, setForwardTo] = useState<IPersonaProps[]>([]);
    // Same as forwardTo, for the Forward for Review page's optional Cc field.
    const [forwardCc, setForwardCc] = useState<IPersonaProps[]>([]);

    useEffect(() => {
        setForwardTo([]);
        setForwardCc([]);
    }, [selectedProject?.nrn]);
    // Mirrors `receivedDate` so the async project-settings load can derive the due date from the
    // current value without re-running (and re-fetching) whenever the user edits the received date.
    const receivedDateRef = useRef<Date | undefined>(undefined);
    receivedDateRef.current = receivedDate;
    // Mirrors `dueDate` so the async project-settings load can derive the forward due date's cap
    // from the current value even when it was set by an earlier user edit (not by this load).
    const dueDateRef = useRef<Date | undefined>(undefined);
    dueDateRef.current = dueDate;
    // The selected project's RFI due-date offset settings, kept so switching emails re-derives the
    // default due date without another settings lookup.
    const rfiSettingsRef = useRef<RfiSettings | null>(null);
    // Set when the user picks or clears the due date. The async project-settings load only applies
    // the derived default if the user has not edited the due date since the request started.
    const isDueDateUserEditedRef = useRef<boolean>(false);
    // Same guard for the Forward for Review page's own due date.
    const isForwardDueDateUserEditedRef = useRef<boolean>(false);
    const [isCloudProject, setIsCloudProject] = useState<boolean>(true);
    // True while the selected project's settings are being fetched. Submission is blocked until the
    // settings (or the failure fallback) have been applied so the request never carries the
    // provisional due date. Date edits stay enabled meanwhile.
    const [isLoadingProjectSettings, setIsLoadingProjectSettings] = useState<boolean>(false);
    const [typeOptions, setTypeOptions] = useState<DetailedKeyword[]>([]);
    const [from, setFrom] = useState<IPersonaProps[]>([]);
    const [to, setTo] = useState<IPersonaProps[]>([]);
    // Reported by FromAndToFluent9Simple (format + required + to/cc-duplicate checks); starts false
    // since From/To are both required and empty on mount.
    const [isFromToValid, setIsFromToValid] = useState<boolean>(false);
    const [selectedType, setSelectedType] = useState<Keyword | null>(null);
    const [senderNumber, setSenderNumber] = useState<string>("");
    const [purposeOptions, setPurposeOptions] = useState<DetailedKeyword[]>([]);
    const [selectedPurpose, setSelectedPurpose] = useState<Keyword | null>(null);
    const [reasonsResponse, setReasonsResponse] = useState<ProjectKeywordsResponse | null>(null);
    const [selectedReasons, setSelectedReasons] = useState<Keyword[]>([]);
    const [disciplinesResponse, setDisciplinesResponse] = useState<ProjectKeywordsResponse | null>(null);
    const [selectedDisciplines, setSelectedDisciplines] = useState<Keyword[]>([]);
    const [keywordsResponse, setKeywordsResponse] = useState<ProjectKeywordsResponse | null>(null);
    const [selectedKeywords, setSelectedKeywords] = useState<Keyword[]>([]);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [selectedContractNrn, setSelectedContractNrn] = useState<string>("");
    const [isLoadingFieldOptions, setIsLoadingFieldOptions] = useState<boolean>(false);
    const fieldOptionsRequestRef = useRef(0);
    const [subject, setSubject] = useState<string>("");
    const [question, setQuestion] = useState<string>("");
    const [internalNotes, setInternalNotes] = useState<string>("");
    const [suggestions, setSuggestions] = useState<string>("");
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
    const [isFiling, setIsFiling] = useState<boolean>(false);
    const [shouldForwardRfi, setShouldForwardRfi] = useState<boolean>(false);
    const prevMailboxItemRef = useRef<MailboxItem | null>(null);
    const [showForwardPanel, setShowForwardPanel] = useState<boolean>(false);

    useEffect(() => {
        setShowForwardPanel(false);
    }, [props.mailboxItem]);
    // bumped whenever the pending auto-populated question is no longer valid (edit, clear, or re-run)
    // so a late-arriving response from an earlier request is never applied
    const questionRequestIdRef = useRef(0);

    const { teamMembers, isLoadingTeamMembers, autoPopulatedFrom, autoPopulatedTo } = useProjectTeamMembers({
        projectNrn: selectedProject?.nrn,
        projectsService: props.projectsService,
        formValidationHelpers: props.formValidationHelpers,
        logger: props.logger,
        onExpiredSession: props.onExpiredSession,
        officeWrapper: props.officeWrapper,
        mailboxItem: props.mailboxItem,
    });

    // Mirrors `question` so the editor change handler can tell a real user edit apart from Froala
    // echoing a programmatic value change (e.g. the reset to "" when switching emails). Without this,
    // the echo would bump questionRequestIdRef and the auto-populated body would be discarded.
    const questionRef = useRef<string>("");
    questionRef.current = question;

    // A newly selected project gets a fresh default due date, so forget any earlier user edit. This
    // is deliberately separate from the settings-loading effect below, which also reruns when callback
    // props change identity (e.g. a parent re-render) and must not clear the guard in that case.
    useEffect(() => {
        isDueDateUserEditedRef.current = false;
        isForwardDueDateUserEditedRef.current = false;
    }, [selectedProject]);

    // The parent re-binds these callbacks on every render; reading them through a ref keeps a parent
    // re-render (e.g. showing the success toast) from refetching the settings.
    const settingsCallbacksRef = useRef({ logger: props.logger, onExpiredSession: props.onExpiredSession });
    settingsCallbacksRef.current = { logger: props.logger, onExpiredSession: props.onExpiredSession };

    // mirrors legacy RfiComponent.onProjectSelected()/getProjectSettings(): isCloudProject is known
    // synchronously from the nrn, while numberEntryMode and the default due date require an NPC
    // project settings lookup
    useEffect(() => {
        if (!selectedProject) {
            setIsCloudProject(true);
            setNumberEntryMode(null);
            rfiSettingsRef.current = null;
            setIsLoadingProjectSettings(false);
            return;
        }

        setIsCloudProject(props.nrnServiceWrapper.isCloudProject(selectedProject.nrn));

        let isCancelled = false;
        setIsLoadingProjectSettings(true);
        (async () => {
            try {
                const projectSettings = await props.projectsService.getProjectSettings(selectedProject.nrn);
                if (!isCancelled) {
                    rfiSettingsRef.current = projectSettings?.rfis ?? null;
                    setNumberEntryMode(projectSettings?.rfis.numberEntryMode ?? null);
                    if (!isDueDateUserEditedRef.current) {
                        const nextDueDate = getDefaultDueDate(receivedDateRef.current, rfiSettingsRef.current);
                        setDueDate(nextDueDate);
                        if (!isForwardDueDateUserEditedRef.current) {
                            setForwardDueDate(getDefaultForwardDueDate(nextDueDate, rfiSettingsRef.current));
                        }
                    } else if (!isForwardDueDateUserEditedRef.current) {
                        setForwardDueDate(getDefaultForwardDueDate(dueDateRef.current, rfiSettingsRef.current));
                    }
                    setIsLoadingProjectSettings(false);
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                if (ExpiredSessionError.isInstanceOf(error)) {
                    settingsCallbacksRef.current.onExpiredSession();
                    return;
                }
                settingsCallbacksRef.current.logger.error(
                    "RfiFluent9Component - error loading project settings",
                    error
                );
                rfiSettingsRef.current = null;
                setNumberEntryMode(null);
                if (!isDueDateUserEditedRef.current) {
                    const nextDueDate = getDefaultDueDate(receivedDateRef.current, null);
                    setDueDate(nextDueDate);
                    if (!isForwardDueDateUserEditedRef.current) {
                        setForwardDueDate(getDefaultForwardDueDate(nextDueDate, null));
                    }
                } else if (!isForwardDueDateUserEditedRef.current) {
                    setForwardDueDate(getDefaultForwardDueDate(dueDateRef.current, null));
                }
                setIsLoadingProjectSettings(false);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [selectedProject, props.nrnServiceWrapper, props.projectsService]);

    // mirrors legacy RfiComponent.populateDefaults(): (re)populate subject, from/to, and classification
    // selections from the current email whenever the selected mailbox item changes
    useEffect(() => {
        const prevMailboxItem = prevMailboxItemRef.current;
        prevMailboxItemRef.current = props.mailboxItem;

        if (props.mailboxItem && prevMailboxItem !== props.mailboxItem) {
            setSubject((props.mailboxItem.subject ?? "").trim());
            const emailReceivedDate = props.officeWrapper.getCurrentEmailDate();
            setReceivedDate(emailReceivedDate);
            // a new email gets a fresh default due date, so any pending settings load may apply its default too
            isDueDateUserEditedRef.current = false;
            const nextDueDate = getDefaultDueDate(emailReceivedDate, rfiSettingsRef.current);
            setDueDate(nextDueDate);
            isForwardDueDateUserEditedRef.current = false;
            setForwardDueDate(getDefaultForwardDueDate(nextDueDate, rfiSettingsRef.current));
            setIsForwardReminderEnabled(false);
            setForwardReminderDays(DEFAULT_REMINDER_DAYS);
            setForwardTo([]);
            setForwardCc([]);
            // Seed From/To from the newly selected email (From = sender, To = recipients), mirroring the
            // legacy component. The picker is controlled by these values and reflects the change
            // automatically. These values come from the hook and change only with the mailbox item, so
            // this runs once per email and never overwrites later user edits.
            setFrom(autoPopulatedFrom);
            setTo(autoPopulatedTo);
            // mirrors legacy RfiComponent.componentDidUpdate(): per-email classification selections must
            // not carry over to the next email in a pinned pane. Reset them while keeping the selected
            // project and the loaded option lists intact.
            setSelectedType(null);
            setSenderNumber("");
            setSelectedPurpose(null);
            setSelectedReasons([]);
            setSelectedDisciplines([]);
            setSelectedKeywords([]);
            setSelectedContractNrn("");
            setInternalNotes("");
            setSuggestions("");
        }
    }, [props.mailboxItem, autoPopulatedFrom, autoPopulatedTo, props.officeWrapper]);

    // Loads the question body from the current email. Kept as its own effect (rather than folded into
    // the reset effect above) so it also reruns when isNL1221Supported flips after the feature flag
    // finishes loading from configuration — a page opened with the cached/stale value must not get
    // stuck showing the plain-text body in the Froala editor once the real value arrives.
    useEffect(() => {
        if (!props.mailboxItem) {
            return;
        }

        setQuestion("");

        const requestId = ++questionRequestIdRef.current;
        (async () => {
            try {
                const { inlineAttachments } = AttachmentDataHelpers.partitionAttachments(
                    props.officeWrapper.getCurrentEmailAttachmentDetails()
                );
                const question = await AttachmentDataHelpers.getHtmlBodyWithInlineAttachments(
                    props.msGraphApiService,
                    props.outlookApiService,
                    props.isNL1221Supported,
                    inlineAttachments
                );
                if (requestId === questionRequestIdRef.current) {
                    setQuestion(question);
                }
            } catch (error) {
                if (requestId === questionRequestIdRef.current) {
                    props.logger.error("RfiFluent9Component - error loading email body for question", error);
                }
            }
        })();
    }, [
        props.mailboxItem,
        props.officeWrapper,
        props.msGraphApiService,
        props.outlookApiService,
        props.isNL1221Supported,
        props.logger,
    ]);

    const getProjects = useCallback(() => props.projectsService.getProjectsSupportingRfis(), [props.projectsService]);

    // Loads the classification/action option lists upfront when the project changes. Type/Reason/
    // Discipline/Keyword apply to every project; Purpose and Contract are always shown. isCloudProject
    // and the number-entry mode remain owned by the project-settings effect above; this handler only
    // seeds selectedProject and the six classification fields. A stale-request guard ensures a slow
    // response for a previously selected project cannot overwrite the current project's options.
    const handleProjectChange = useCallback(
        async (project?: IProjectData) => {
            const requestId = ++fieldOptionsRequestRef.current;
            setSelectedProject(project);
            setSelectedType(null);
            setSelectedPurpose(null);
            setSelectedReasons([]);
            setSelectedDisciplines([]);
            setSelectedKeywords([]);
            setSelectedContractNrn("");
            // team members differ per project, so From/To go back to the email-derived defaults
            setFrom(autoPopulatedFrom);
            setTo(autoPopulatedTo);

            if (!project) {
                setTypeOptions([]);
                setPurposeOptions([]);
                setReasonsResponse(null);
                setDisciplinesResponse(null);
                setKeywordsResponse(null);
                setContracts([]);
                setIsLoadingFieldOptions(false);
                return;
            }

            const projectNrn = project.nrn;
            setIsLoadingFieldOptions(true);

            // mirrors legacy getContracts(): a contracts failure resolves to an empty list so it can
            // never fail the batch below.
            const loadContracts = async (): Promise<Contract[]> => {
                try {
                    return ProjectHelper.sortContracts(await props.projectsService.getContracts(projectNrn));
                } catch {
                    return [];
                }
            };

            try {
                // mirrors legacy RfiComponent.loadKeywords(): every list loads together and the whole
                // batch is fail-fast. The Fluent 9 pickers take their options as props, so
                // Reason/Discipline/Keyword/Purpose/Contract load here rather than lazily.
                const purposePromise = props.rfiApiService.getRfiActionKeywords(projectNrn, WorkflowActionType.Log);
                const contractsPromise = loadContracts();

                const [
                    typesResp,
                    reasonsResp,
                    disciplinesResp,
                    keywordsResp,
                    purposesResp,
                    sortedContracts,
                ] = await Promise.all([
                    props.projectsService.getProjectKeywords(projectNrn, KeywordListType.RfiTypes),
                    props.projectsService.getProjectKeywords(projectNrn, KeywordListType.RfiReasons),
                    props.projectsService.getProjectKeywords(projectNrn, KeywordListType.RfiDisciplines),
                    props.projectsService.getProjectKeywords(projectNrn, KeywordListType.RfiKeywords),
                    purposePromise,
                    contractsPromise,
                ]);

                if (requestId !== fieldOptionsRequestRef.current) {
                    return;
                }

                setTypeOptions(ProjectHelper.sortKeywords(typesResp));
                setReasonsResponse(reasonsResp);
                setDisciplinesResponse(disciplinesResp);
                setKeywordsResponse(keywordsResp);
                setContracts(sortedContracts);

                if (purposesResp) {
                    const sortedPurposes = ProjectHelper.sortKeywords(purposesResp);
                    setPurposeOptions(sortedPurposes);
                    // mirrors legacy default: the first (sorted) action keyword is "Request for Information"
                    if (sortedPurposes.length > 0) {
                        setSelectedPurpose({ name: sortedPurposes[0].name, type: sortedPurposes[0].type });
                    }
                } else {
                    setPurposeOptions([]);
                }
            } catch (error) {
                if (requestId !== fieldOptionsRequestRef.current) {
                    return;
                }
                if (ExpiredSessionError.isInstanceOf(error)) {
                    props.onExpiredSession();
                    return;
                }
                // mirrors legacy clearKeywords() + handleKeywordError(): blank every list, one toast
                props.logger.error("RfiFluent9Component - error loading field options", error);
                setTypeOptions([]);
                setReasonsResponse(null);
                setDisciplinesResponse(null);
                setKeywordsResponse(null);
                setPurposeOptions([]);
                setContracts([]);
                props.onShowToast(props.translate("RFI.ERRORS.LOADING_KEYWORDS_FAILED") as string, "error");
            } finally {
                if (requestId === fieldOptionsRequestRef.current) {
                    setIsLoadingFieldOptions(false);
                }
            }
        },
        [
            autoPopulatedFrom,
            autoPopulatedTo,
            props.nrnServiceWrapper,
            props.projectsService,
            props.rfiApiService,
            props.logger,
            props.onExpiredSession,
            props.onShowToast,
            props.translate,
        ]
    );

    const reasonOptionNames = useMemo(() => sortDetailedKeywords(reasonsResponse?.items ?? []).map((k) => k.name), [
        reasonsResponse,
    ]);
    const disciplineOptionNames = useMemo(
        () => sortDetailedKeywords(disciplinesResponse?.items ?? []).map((k) => k.name),
        [disciplinesResponse]
    );
    const keywordOptionNames = useMemo(() => sortDetailedKeywords(keywordsResponse?.items ?? []).map((k) => k.name), [
        keywordsResponse,
    ]);
    const typeDropdownOptions = useMemo<NewformaDropdownOption[]>(
        () => typeOptions.map((t) => ({ key: t.name, text: t.name })),
        [typeOptions]
    );
    const purposeDropdownOptions = useMemo<NewformaDropdownOption[]>(
        () => purposeOptions.map((p) => ({ key: p.name, text: p.name })),
        [purposeOptions]
    );
    const contractDropdownOptions = useMemo<NewformaDropdownOption[]>(
        () => [
            { key: "", text: props.translate("RFI.NO_OPTION") as string },
            ...contracts.map((c) => ({ key: c.contractNrn, text: c.subject })),
        ],
        [contracts, props]
    );

    // The "no options" hint shown inside the multi-select pickers depends only on whether a given
    // keyword list allows custom values, so both outcomes are computed once here and the per-field
    // ternary (on each list's allowCustom flag) is inlined at the call sites below.
    const pressEnterText = props.translate("FLUENT9.PRESS_ENTER") as string;
    const customValuesNotAllowedText = (
        <span className={styles.noOptionWarning}>
            <ErrorCircle12Filled />
            {props.translate("FLUENT9.CUSTOM_VALUES_NOT_ALLOWED") as string}
        </span>
    );

    const fieldsDisabled = isFiling || isLoadingProjects || isLoadingFieldOptions || !selectedProject;

    const isNumberManualEntry = !isCloudProject && numberEntryMode === NumberEntry.manual;

    // mirrors legacy RfiComponent.onFormSubmit()/moveToForwardRfi(): unchecked files the RFI directly,
    // checked files the RFI and proceeds to Forward for Review. The Forward for Review page itself is
    // currently a placeholder panel; field migration and filing happen in a follow-up story.
    // The RFI form fields that are not yet migrated to Fluent 9
    // (received/due dates, from/to, question, etc.) are supplied here as hardcoded,
    // schema-valid values so that hitting submit exercises the same end-to-end filing flow as the
    // legacy RfiComponent and is accepted by the server.
    const onPrimaryAction = useCallback(async () => {
        if (
            !selectedProject ||
            !receivedDate ||
            isFiling ||
            isLoadingProjects ||
            isLoadingProjectSettings ||
            isLoadingTeamMembers
        ) {
            return;
        }

        if (shouldForwardRfi) {
            setShowForwardPanel(true);
            return;
        }

        setIsFiling(true);
        try {
            const messageNrn = props.officeWrapper.getCurrentMessageNrn();

            const rfiParams: LogRfiParams = {
                subject: subject.trim(),
                receivedDate: receivedDate.toISOString(),
                dueDate: dueDate?.toISOString(),
                from: props.formValidationHelpers.mapPersonaPropsToContact(from)[0],
                to: props.formValidationHelpers.mapPersonaPropsToContact(to),
                question: question.trim(),
                number: number.trim() || undefined,
                urgent: isCloudProject ? false : undefined,
                types: selectedType ? [selectedType] : undefined,
                senderNumber: senderNumber.trim() || undefined,
                disciplines: selectedDisciplines,
                reasons: selectedReasons,
                keywords: selectedKeywords,
                via: isCloudProject ? undefined : { name: "Email", type: "transferMethod" },
                purpose: selectedPurpose ?? undefined,
                contract: selectedContractNrn || undefined,
                internalNotes: internalNotes.trim() || undefined,
                suggestion: suggestions.trim() || undefined,
                // mirrors legacy: a reminder is only meaningful with a due date and only supported by NPC
                reminder: isReminderEnabled && !!dueDate && !isCloudProject ? reminderDays : undefined,
                attachments: [],
            };

            await props.rfiApiService.fileEmailAsRfi(
                selectedProject.nrn,
                rfiParams,
                messageNrn,
                [],
                () => {
                    /* no attachments to track */
                },
                isCloudProject,
                props.isNL1221Supported,
                shouldForwardRfi,
                props.culture
            );

            try {
                const projectAsAny = selectedProject as any;
                props.analyticsManager.recordSmartFilingEvents(
                    projectAsAny.suggestedProject,
                    projectAsAny.suggestionIndex
                );
                await props.smartFilingManager.addToFiledHistory(
                    { key: selectedProject.nrn, name: selectedProject.displayName },
                    props.mailboxItem?.conversationId as string,
                    props.mailboxItem?.sender.emailAddress as string
                );
            } catch (error) {
                props.logger.error("RfiFluent9Component - failed to update smart filing history", error);
            }

            props.onShowToast(props.translate("RFI.SUCCESS_MESSAGE") as string, "success");
            setNumber("");
            setSenderNumber("");
            setSelectedReasons([]);
            setSelectedDisciplines([]);
            setSelectedKeywords([]);
            setSelectedContractNrn("");
            setInternalNotes("");
            setSuggestions("");
        } catch (error) {
            props.logger.error("RfiFluent9Component - error filing RFI", error);

            if (ExpiredSessionError.isInstanceOf(error)) {
                props.onExpiredSession();
                return;
            }

            const apiError = error as any;
            if (apiError?.status === 405) {
                props.onShowToast(props.translate("RFI.ERRORS.PERMISSIONS_INSUFFICIENT") as string, "error");
                return;
            }

            if (apiError?.level === ApiRequestErrorLevel.ERROR && apiError.messageToDisplay) {
                props.onShowToast(props.translate(apiError.messageToDisplay) as string, "error");
                return;
            }

            props.onShowToast(props.translate("RFI.ERRORS.LOG_RFI_FAILED_GENERIC") as string, "error");
        } finally {
            setIsFiling(false);
        }
    }, [
        selectedProject,
        receivedDate,
        dueDate,
        isReminderEnabled,
        reminderDays,
        isFiling,
        isLoadingProjects,
        isLoadingProjectSettings,
        isLoadingTeamMembers,
        shouldForwardRfi,
        subject,
        question,
        internalNotes,
        suggestions,
        number,
        isCloudProject,
        selectedType,
        senderNumber,
        selectedPurpose,
        selectedReasons,
        selectedDisciplines,
        selectedKeywords,
        selectedContractNrn,
        from,
        to,
        props,
    ]);

    const handleClear = useCallback(() => {
        questionRequestIdRef.current += 1;
        handleProjectChange(undefined);
        setSubject("");
        setNumber("");
        setReceivedDate(undefined);
        setDueDate(undefined);
        setIsReminderEnabled(true);
        setReminderDays(DEFAULT_REMINDER_DAYS);
        wasReminderAutoDisabledRef.current = false;
        isForwardDueDateUserEditedRef.current = false;
        setForwardDueDate(undefined);
        setIsForwardReminderEnabled(false);
        setForwardReminderDays(DEFAULT_REMINDER_DAYS);
        setForwardTo([]);
        setForwardCc([]);
        setQuestion("");
        setInternalNotes("");
        setSuggestions("");
        setShouldForwardRfi(false);
        setFrom([]);
        setTo([]);
        setSenderNumber("");
    }, [handleProjectChange]);

    // mirrors legacy onFormSubmit()'s forwarding branch: builds the same RFI payload as
    // onPrimaryAction but attaches forwardRfiData, so the server creates the RFI and forwards it in
    // a single call. The Forward for Review page doesn't have its own purpose/remarks fields yet, so
    // the main form's purpose is reused and remarks are left blank until that follow-up story lands.
    const handleForwardSubmit = useCallback(async () => {
        if (!selectedProject || !receivedDate || !selectedPurpose || isFiling) {
            return;
        }

        setIsFiling(true);
        try {
            const messageNrn = props.officeWrapper.getCurrentMessageNrn();

            // mirrors legacy onFormSubmit(): reviewDueDate is synced to the current time-of-day (not
            // midnight) so the day boundary doesn't shift when the server converts it to UTC.
            const forwardReviewDate = forwardDueDate ? DateHelper.adjustTime(new Date(forwardDueDate)) : undefined;

            const forwardRfiData: LogForwardRfiRequest = {
                version: "0",
                purpose: selectedPurpose,
                via: "info-exchange",
                reviewDueDate: forwardReviewDate,
                remarks: undefined,
                to: props.formValidationHelpers.mapPersonaPropsToContact(forwardTo),
                cc: props.formValidationHelpers.mapPersonaPropsToContact(forwardCc),
                reminder:
                    isForwardReminderEnabled && !!forwardDueDate && !isCloudProject ? forwardReminderDays : undefined,
            };

            const rfiParams: LogRfiParams = {
                subject: subject.trim(),
                receivedDate: receivedDate.toISOString(),
                dueDate: dueDate?.toISOString(),
                from: props.formValidationHelpers.mapPersonaPropsToContact(from)[0],
                to: props.formValidationHelpers.mapPersonaPropsToContact(to),
                question: question.trim(),
                number: number.trim() || undefined,
                urgent: isCloudProject ? false : undefined,
                types: selectedType ? [selectedType] : undefined,
                senderNumber: senderNumber.trim() || undefined,
                disciplines: selectedDisciplines,
                reasons: selectedReasons,
                keywords: selectedKeywords,
                via: isCloudProject ? undefined : { name: "Email", type: "transferMethod" },
                purpose: selectedPurpose,
                contract: selectedContractNrn || undefined,
                reminder: isReminderEnabled && !!dueDate && !isCloudProject ? reminderDays : undefined,
                attachments: [],
                forwardRfiData,
            };

            await props.rfiApiService.fileEmailAsRfi(
                selectedProject.nrn,
                rfiParams,
                messageNrn,
                [],
                () => {
                    /* no attachments to track */
                },
                isCloudProject,
                props.isNL1221Supported,
                true,
                props.culture
            );

            try {
                const projectAsAny = selectedProject as any;
                props.analyticsManager.recordSmartFilingEvents(
                    projectAsAny.suggestedProject,
                    projectAsAny.suggestionIndex
                );
                await props.smartFilingManager.addToFiledHistory(
                    { key: selectedProject.nrn, name: selectedProject.displayName },
                    props.mailboxItem?.conversationId as string,
                    props.mailboxItem?.sender.emailAddress as string
                );
            } catch (error) {
                props.logger.error("RfiFluent9Component - failed to update smart filing history", error);
            }

            props.onShowToast(props.translate("RFI.FORWARD_RFI.CREATE_SUCCESSFUL") as string, "success");
            setShowForwardPanel(false);
            setNumber("");
            setSenderNumber("");
            setSelectedReasons([]);
            setSelectedDisciplines([]);
            setSelectedKeywords([]);
            setSelectedContractNrn("");
        } catch (error) {
            props.logger.error("RfiFluent9Component - error filing forwarded RFI", error);
            setShowForwardPanel(false);

            if (ExpiredSessionError.isInstanceOf(error)) {
                props.onExpiredSession();
                return;
            }

            const apiError = error as any;
            if (apiError?.status === 405) {
                props.onShowToast(props.translate("RFI.ERRORS.PERMISSIONS_INSUFFICIENT") as string, "error");
                return;
            }

            if (apiError?.level === ApiRequestErrorLevel.ERROR && apiError.messageToDisplay) {
                props.onShowToast(props.translate(apiError.messageToDisplay) as string, "error");
                return;
            }

            props.onShowToast(props.translate("RFI.FORWARD_RFI.CREATE_FAILED") as string, "error");
        } finally {
            setIsFiling(false);
        }
    }, [
        selectedProject,
        receivedDate,
        dueDate,
        isReminderEnabled,
        reminderDays,
        isFiling,
        subject,
        question,
        number,
        isCloudProject,
        selectedType,
        senderNumber,
        selectedPurpose,
        selectedReasons,
        selectedDisciplines,
        selectedKeywords,
        selectedContractNrn,
        from,
        to,
        forwardDueDate,
        forwardTo,
        forwardCc,
        isForwardReminderEnabled,
        forwardReminderDays,
        props,
    ]);

    const isActionDisabled = isFiling || isLoadingProjects;

    const isFormValid =
        !!selectedProject &&
        // the default due date is only final once the project's settings (or the fallback) are applied
        !isLoadingProjectSettings &&
        // From/To are seeded from the email before the team list arrives; keep Submit off until it does
        !isLoadingTeamMembers &&
        !!subject.trim() &&
        !!receivedDate &&
        // mirrors legacy isFormValid(): due date is not required for NPC projects
        (!isCloudProject || !!dueDate) &&
        !!question.trim() &&
        (!isNumberManualEntry || !!number.trim()) &&
        !!selectedType &&
        !!selectedPurpose &&
        !!props.mailboxItem &&
        isFromToValid;

    if (showForwardPanel) {
        return (
            <ForwardRfiFluent9
                isFiling={isFiling}
                dueDate={forwardDueDate}
                onDueDateChange={(date) => {
                    isForwardDueDateUserEditedRef.current = true;
                    setForwardDueDate(date);
                }}
                isReminderEnabled={isForwardReminderEnabled}
                onReminderEnabledChange={setIsForwardReminderEnabled}
                reminderDays={forwardReminderDays}
                onReminderDaysChange={setForwardReminderDays}
                teamMembers={teamMembers}
                isLoadingTeamMembers={isLoadingTeamMembers}
                formValidationHelpers={props.formValidationHelpers}
                toValue={forwardTo}
                onToChange={setForwardTo}
                ccValue={forwardCc}
                onCcChange={setForwardCc}
                onBack={() => setShowForwardPanel(false)}
                onClear={() => {
                    handleClear();
                    setShowForwardPanel(false);
                }}
                onSubmit={handleForwardSubmit}
            />
        );
    }

    return (
        <FluentProvider theme={theme} style={{ display: "contents" }}>
            <div className={styles.root}>
                <div className={styles.content}>
                    <ProjectPickerWithLoader
                        getProjects={getProjects}
                        cacheKey={ProjectsCacheKeys.rfisCacheName}
                        smartFilingManager={props.smartFilingManager}
                        invalidateCacheService={props.invalidateCacheService}
                        logger={props.logger}
                        officeWrapper={props.officeWrapper}
                        selectedProject={selectedProject}
                        onProjectChange={handleProjectChange}
                        onLoadingChange={setIsLoadingProjects}
                        disabled={isFiling}
                    />
                    <NewformaInput
                        label={props.translate("RFI.SUBJECT_LABEL") as string}
                        required={true}
                        id="rfi-subject"
                        data-testid="rfi-subject-input"
                        fieldDataTestId="rfi-subject-field"
                        value={subject}
                        onChange={(_e, data) => setSubject(data.value)}
                        disabled={isFiling || isLoadingProjects}
                        maxLength={256}
                        autoComplete="off"
                        placeholder={props.translate("RFI.SUBJECT_PLACEHOLDER") as string}
                    />
                    <NewformaInput
                        label={props.translate("RFI.NUMBER_LABEL") as string}
                        required={isNumberManualEntry}
                        id="rfi-number"
                        data-testid="rfi-number-input"
                        fieldDataTestId="rfi-number-field"
                        value={number}
                        onChange={(_e, data) => setNumber(data.value)}
                        disabled={isFiling || isLoadingProjects}
                        maxLength={256}
                        autoComplete="off"
                        placeholder={
                            isNumberManualEntry
                                ? (props.translate("RFI.NUMBER_MANUAL_PLACEHOLDER") as string)
                                : (props.translate("RFI.NUMBER_PLACEHOLDER") as string)
                        }
                    />
                    <div className={styles.reminderSection}>
                        <div className={styles.dateSection}>
                            <div className={styles.dateRow}>
                                <Field
                                    label={props.translate("RFI.RECEIVED_DATE_LABEL") as string}
                                    required={true}
                                    className={styles.dateField}
                                    data-testid="rfi-received-date-field"
                                >
                                    <NewformaDatePicker
                                        data-testid="rfi-received-date-picker"
                                        className={styles.datePicker}
                                        value={receivedDate}
                                        onSelectDate={(date) => setReceivedDate(date ?? undefined)}
                                        maxDate={dueDate}
                                        disabled={isFiling || isLoadingProjects}
                                        showGoToToday={true}
                                        aria-label={props.translate("RFI.RECEIVED_DATE_LABEL") as string}
                                    />
                                </Field>
                                <Field
                                    label={props.translate("RFI.DUE_DATE_LABEL") as string}
                                    required={isCloudProject}
                                    className={styles.dateField}
                                    data-testid="rfi-due-date-field"
                                >
                                    <NewformaDatePicker
                                        data-testid="rfi-due-date-picker"
                                        className={styles.datePicker}
                                        value={dueDate}
                                        onSelectDate={(date) => {
                                            isDueDateUserEditedRef.current = true;
                                            setDueDate(date ?? undefined);
                                            if (!date) {
                                                setIsReminderEnabled(false);
                                                wasReminderAutoDisabledRef.current = true;
                                            } else if (wasReminderAutoDisabledRef.current) {
                                                setIsReminderEnabled(true);
                                                wasReminderAutoDisabledRef.current = false;
                                            }
                                        }}
                                        minDate={dueDateMinDate}
                                        disabled={isFiling || isLoadingProjects}
                                        showGoToToday={true}
                                        aria-label={props.translate("RFI.DUE_DATE_LABEL") as string}
                                    />
                                </Field>
                            </div>
                            <NewformaLink
                                className={styles.clearDateLink}
                                dataTestId="rfi-clear-due-date-link"
                                disabled={isFiling || isLoadingProjects}
                                onClick={() => {
                                    isDueDateUserEditedRef.current = true;
                                    setDueDate(undefined);
                                    setIsReminderEnabled(false);
                                    wasReminderAutoDisabledRef.current = true;
                                }}
                            >
                                {props.translate("DATES.CLEAR_DATE") as string}
                            </NewformaLink>
                        </div>
                        <ReminderFluent9
                            testIdPrefix="rfi-reminder"
                            label={props.translate("SHARED.REMINDER_COMPONENT.TOGGLE") as string}
                            daysLabel={props.translate("SHARED.REMINDER_COMPONENT.DAYS_LABEL") as string}
                            checked={isReminderEnabled}
                            onCheckedChange={(checked) => {
                                wasReminderAutoDisabledRef.current = false;
                                setIsReminderEnabled(checked);
                            }}
                            switchDisabled={isFiling || isLoadingProjects || !dueDate || isReminderLimited}
                            days={reminderDays}
                            onDaysChange={setReminderDays}
                            min={MIN_REMINDER_DAYS}
                            max={maxReminderDays}
                            stepperDisabled={isFiling || isLoadingProjects}
                        />
                    </div>
                    <div className={styles.typeSenderRow}>
                        <Field
                            label={props.translate("RFI.TYPE.LABEL") as string}
                            required={true}
                            className={styles.typeField}
                        >
                            <NewformaDropdown
                                data-testid="rfi-type-dropdown"
                                placeholder={
                                    isLoadingFieldOptions
                                        ? (props.translate("RFI.TYPE.LOADING") as string)
                                        : !!selectedProject && !isLoadingFieldOptions && typeOptions.length === 0
                                        ? (props.translate("SHARED.KEYWORD_DROPDOWN.NO_ITEMS") as string)
                                        : (props.translate("RFI.TYPE.PLACEHOLDER") as string)
                                }
                                disabled={
                                    fieldsDisabled ||
                                    (!!selectedProject && !isLoadingFieldOptions && typeOptions.length === 0)
                                }
                                value={selectedType?.name ?? ""}
                                selectedOptions={selectedType ? [selectedType.name] : []}
                                onOptionSelect={(_e, data) => {
                                    const match = typeOptions.find((t) => t.name === data.optionValue);
                                    setSelectedType(match ? { name: match.name, type: match.type } : null);
                                }}
                                options={typeDropdownOptions}
                            />
                        </Field>
                        <NewformaInput
                            label={props.translate("SUBMITTALS.SENDER_NUMBER_LABEL") as string}
                            fieldDataTestId="rfi-sender-number-field"
                            data-testid="rfi-sender-number-input"
                            className={styles.senderNumberField}
                            value={senderNumber}
                            onChange={(_e, data) => setSenderNumber(data.value)}
                            disabled={isFiling || isLoadingProjects}
                            maxLength={256}
                            placeholder={props.translate("SUBMITTALS.SENDER_NUMBER_PLACEHOLDER") as string}
                        />
                    </div>
                    <FromAndToFluent9Simple
                        hasFrom={true}
                        hasTo={true}
                        hasCc={false}
                        teamMembers={teamMembers}
                        formValidationHelpers={props.formValidationHelpers}
                        disabled={isFiling || !selectedProject || isLoadingTeamMembers || isLoadingProjects}
                        isLoadingTeamMembers={isLoadingTeamMembers}
                        isFromRequired={true}
                        isToRequired={true}
                        fromValue={from}
                        toValue={to}
                        onFromChange={setFrom}
                        onToChange={setTo}
                        onValidationChange={setIsFromToValid}
                    />
                    <Field
                        label={props.translate("RFI.QUESTION_LABEL") as string}
                        required={true}
                        data-testid="rfi-question-field"
                    >
                        {props.isNL1221Supported ? (
                            <EditorFluent9
                                value={question}
                                onRemarksUpdate={(content) => {
                                    const next = content ?? "";
                                    if (next === questionRef.current) {
                                        // Froala echoing the value we just set; not a user edit, so the
                                        // pending auto-populate request must stay valid.
                                        return;
                                    }
                                    questionRequestIdRef.current += 1;
                                    setQuestion(next);
                                }}
                                configService={props.configService}
                                isFiling={isFiling}
                            />
                        ) : (
                            <Textarea
                                data-testid="rfi-question-textarea"
                                value={question}
                                onChange={(_e, data) => {
                                    questionRequestIdRef.current += 1;
                                    setQuestion(data.value);
                                }}
                                disabled={isFiling || isLoadingProjects}
                                resize="vertical"
                                rows={5}
                                maxLength={65000}
                            />
                        )}
                    </Field>
                    <Field
                        label={props.translate("RFI.SUGGESTIONS_LABEL") as string}
                        data-testid="rfi-suggestions-field"
                    >
                        <Textarea
                            data-testid="rfi-suggestions-textarea"
                            value={suggestions}
                            onChange={(_e, data) => setSuggestions(data.value)}
                            disabled={isFiling || isLoadingProjects}
                            resize="vertical"
                            rows={3}
                            maxLength={65000}
                            placeholder={props.translate("RFI.SUGGESTIONS_PLACEHOLDER") as string}
                        />
                    </Field>
                    <Field
                        label={props.translate("RFI.INTERNAL_NOTES_LABEL") as string}
                        data-testid="rfi-internal-notes-field"
                    >
                        <Textarea
                            data-testid="rfi-internal-notes-textarea"
                            value={internalNotes}
                            onChange={(_e, data) => setInternalNotes(data.value)}
                            disabled={isFiling || isLoadingProjects}
                            resize="vertical"
                            rows={3}
                            maxLength={65000}
                            placeholder={props.translate("RFI.INTERNAL_NOTES_PLACEHOLDER") as string}
                        />
                    </Field>
                    <Field label={props.translate("RFI.PURPOSE.LABEL") as string} required={true}>
                        <NewformaDropdown
                            data-testid="rfi-purpose-dropdown"
                            placeholder={
                                isLoadingFieldOptions
                                    ? (props.translate("RFI.PURPOSE.LOADING") as string)
                                    : !!selectedProject && !isLoadingFieldOptions && purposeOptions.length === 0
                                    ? (props.translate("SHARED.KEYWORD_DROPDOWN.NO_ITEMS") as string)
                                    : (props.translate("RFI.PURPOSE.PLACEHOLDER") as string)
                            }
                            disabled={
                                fieldsDisabled ||
                                (!!selectedProject && !isLoadingFieldOptions && purposeOptions.length === 0)
                            }
                            value={selectedPurpose?.name ?? ""}
                            selectedOptions={selectedPurpose ? [selectedPurpose.name] : []}
                            onOptionSelect={(_e, data) => {
                                const match = purposeOptions.find((p) => p.name === data.optionValue);
                                setSelectedPurpose(match ? { name: match.name, type: match.type } : null);
                            }}
                            options={purposeDropdownOptions}
                        />
                    </Field>
                    <MultiSelectTagPicker
                        label={props.translate("RFI.REASON_LABEL") as string}
                        testid="rfi-reason-picker"
                        options={reasonOptionNames}
                        selectedOptions={selectedReasons.map((k) => k.name)}
                        onSelectionChange={(names) =>
                            setSelectedReasons(resolveSelectedKeywords(names, selectedReasons, reasonsResponse))
                        }
                        allowCustomInput={!!reasonsResponse?.allowCustom}
                        required={false}
                        disabled={fieldsDisabled}
                        isLoading={isLoadingFieldOptions}
                        placeholder={
                            isLoadingFieldOptions
                                ? (props.translate("RFI.REASON_LOADING") as string)
                                : (props.translate("FLUENT9.ENTER_REASON") as string)
                        }
                        noOptionText={reasonsResponse?.allowCustom ? pressEnterText : customValuesNotAllowedText}
                    />
                    <MultiSelectTagPicker
                        label={props.translate("RFI.DISCIPLINE_LABEL") as string}
                        testid="rfi-discipline-picker"
                        options={disciplineOptionNames}
                        selectedOptions={selectedDisciplines.map((k) => k.name)}
                        onSelectionChange={(names) =>
                            setSelectedDisciplines(
                                resolveSelectedKeywords(names, selectedDisciplines, disciplinesResponse)
                            )
                        }
                        allowCustomInput={!!disciplinesResponse?.allowCustom}
                        required={false}
                        disabled={fieldsDisabled}
                        isLoading={isLoadingFieldOptions}
                        placeholder={
                            isLoadingFieldOptions
                                ? (props.translate("RFI.DISCIPLINE_LOADING") as string)
                                : (props.translate("FLUENT9.ENTER_DISCIPLINES") as string)
                        }
                        noOptionText={disciplinesResponse?.allowCustom ? pressEnterText : customValuesNotAllowedText}
                    />
                    <MultiSelectTagPicker
                        label={props.translate("RFI.KEYWORDS_LABEL") as string}
                        testid="rfi-keyword-picker"
                        options={keywordOptionNames}
                        selectedOptions={selectedKeywords.map((k) => k.name)}
                        onSelectionChange={(names) =>
                            setSelectedKeywords(resolveSelectedKeywords(names, selectedKeywords, keywordsResponse))
                        }
                        allowCustomInput={!!keywordsResponse?.allowCustom}
                        required={false}
                        disabled={fieldsDisabled}
                        isLoading={isLoadingFieldOptions}
                        placeholder={
                            isLoadingFieldOptions
                                ? (props.translate("RFI.KEYWORDS_LOADING") as string)
                                : (props.translate("FLUENT9.ENTER_KEYWORDS") as string)
                        }
                        noOptionText={keywordsResponse?.allowCustom ? pressEnterText : customValuesNotAllowedText}
                    />
                    <Field label={props.translate("RFI.CONTRACT.LABEL") as string}>
                        <NewformaDropdown
                            data-testid="rfi-contract-dropdown"
                            placeholder={
                                isLoadingFieldOptions
                                    ? (props.translate("RFI.CONTRACT.LOADING_ELLIPSIS") as string)
                                    : !selectedProject
                                    ? (props.translate("RFI.CONTRACT.PLACEHOLDER") as string)
                                    : contracts.length === 0
                                    ? (props.translate("RFI.CONTRACT.NO_KEYWORDS") as string)
                                    : (props.translate("RFI.CONTRACT.PLACEHOLDER") as string)
                            }
                            disabled={fieldsDisabled || contracts.length === 0}
                            value={
                                selectedContractNrn
                                    ? contracts.find((c) => c.contractNrn === selectedContractNrn)?.subject ?? ""
                                    : ""
                            }
                            selectedOptions={[selectedContractNrn]}
                            onOptionSelect={(_e, data) => setSelectedContractNrn(data.optionValue ?? "")}
                            options={contractDropdownOptions}
                        />
                    </Field>
                    <div className={styles.forwardCard} data-testid="rfi-forward-card">
                        <div className={styles.forwardCheckbox}>
                            <NewformaCheckbox
                                checked={shouldForwardRfi}
                                onChange={(_event, data) => setShouldForwardRfi(data.checked === true)}
                                disabled={isFiling || isLoadingProjects}
                                dataTestId="rfi-forward-checkbox"
                                ariaLabel={props.translate("RFI.FORWARD_RFI.FORWARD") as string}
                            />
                        </div>
                        <div className={styles.forwardTextColumn}>
                            <Text className={styles.forwardLabel}>
                                {props.translate("RFI.FORWARD_RFI.FORWARD") as string}
                            </Text>
                            <Text className={styles.forwardSubtext}>
                                {props.translate("RFI.FORWARD_RFI.FORWARD_SUBTEXT") as string}
                            </Text>
                        </div>
                    </div>
                </div>
                {/* Footer Section */}
                <div className={styles.footer}>
                    <NewformaButton
                        appearance="subtle"
                        testid="rfi-clear-button"
                        disabled={isActionDisabled}
                        onClick={handleClear}
                        style={{ marginRight: "auto" }}
                    >
                        {props.translate("FLUENT9.CLEAR_FORM") as string}
                    </NewformaButton>
                    <NewformaButton
                        appearance="primary"
                        testid="rfi-submit-button"
                        disabled={isActionDisabled || !isFormValid}
                        onClick={onPrimaryAction}
                    >
                        {shouldForwardRfi
                            ? (props.translate("RFI.FORWARD_RFI.CONTINUE_BUTTON") as string)
                            : (props.translate("RFI.SUBMIT") as string)}
                    </NewformaButton>
                </div>
            </div>
        </FluentProvider>
    );
};

export default withLocalize(RfiFluent9Inner);
